import {
  MATCHING_DETAILS_REQUEST,
  MATCHING_DETAILS_SUCCESS,
  MATCHING_DETAILS_FAILURE,
  MATCHING_SEARCH_REQUEST,
  MATCHING_SEARCH_SUCCESS,
  MATCHING_SEARCH_FAILURE,
  MATCHING_SHORTLIST_REQUEST,
  MATCHING_SHORTLIST_SUCCESS,
  MATCHING_SHORTLIST_FAILURE,
  MATCHING_ADD_SHORTLIST_REQUEST,
  MATCHING_ADD_SHORTLIST_SUCCESS,
  MATCHING_ADD_SHORTLIST_FAILURE,
  MATCHING_REMOVE_SHORTLIST_REQUEST,
  MATCHING_REMOVE_SHORTLIST_SUCCESS,
  MATCHING_REMOVE_SHORTLIST_FAILURE,
  MATCHING_AGGREGATES_SUCCESS,
  MATCHING_COMPANY_FILTERS_SUCCESS,
  MATCHING_CLEAR,
  MATCHING_SET_PAGE,
  MATCHING_MARK_VIEWED,
  MATCHING_REJECTED_REQUEST,
  MATCHING_REJECTED_SUCCESS,
  MATCHING_REJECTED_FAILURE,
  MATCHING_SKIP_CANDIDATE,
  MATCHING_RESTORE_REJECTED_REQUEST,
  MATCHING_RESTORE_REJECTED_SUCCESS,
  MATCHING_RESTORE_REJECTED_FAILURE,
  MATCHING_TG_SUGGESTIONS_REQUEST,
  MATCHING_TG_SUGGESTIONS_SUCCESS,
  MATCHING_TG_SUGGESTIONS_FAILURE,
} from '../actions/actionTypes';
import { EvidenceRankMeta, MatchingAction } from '../actions/matchingActions';

interface SearchSliceState {
  candidates: any[];
  candidatesLoading: boolean;
  candidatesError: string | null;
  pagination: {
    total_results: number;
    total_pages: number;
    page_size: number;
    page?: number;
  };
  currentPage: number;
  parsedJd: any | null;
  searchDebug: any | null;
  sortMode: string | null;
  /** Model coverage for this pool — drives the honest "where scored" sort label. */
  m6: { enabled: boolean; scored_in_pool: number; pool_size: number } | null;
  evidenceRank: EvidenceRankMeta | null;
  /** M5-002: search-session id minted by the server per distinct search (page
   * requests on the same cached search share it). Threaded into shortlist/
   * skip/view dispatches so search_session_id populates on the event tables. */
  sessionId: string | null;
  /** The record of the latest response (server searchViews.service.ts); null
   * when the server sent none. Its cards carry it as `_viewId`. */
  viewId: string | null;
}

interface MatchingState {
  jobDetails: any | null;
  detailsLoading: boolean;
  detailsError: string | null;
  /**
   * ONE search slice (2026-07-29). Replaces the former `browse` + `ranked`
   * slices: the page is a single search surface where Ranked / Mavi Intelligence
   * / Date are *sort orders over the same filtered pool*, not separate engines
   * with their own result sets (MAVI Search Architecture Brief §3).
   */
  search: SearchSliceState;
  shortlist: any[];
  shortlistIds: string[];
  shortlistCount: number;
  shortlistLoading: boolean;
  /** Rejected ("Not a fit") candidates for this opportunity — persistent DB
   * list from GET /:jobOpeningId/rejected. Excluded from every search pool
   * server-side; surfaced (with Restore) in the review rail. */
  rejected: any[];
  rejectedIds: string[];
  rejectedCount: number;
  rejectedLoading: boolean;
  /** True once the first Rejected read has answered (2026-10-28): the tray says "Loading…" only
   * before it, so a background reload over an empty list doesn't flash. */
  rejectedLoaded: boolean;
  /**
   * Restores asked this session, keyed like `shortlistIntent` (`${environment}:${opening}|${id}`,
   * 2026-10-28): the restore is optimistic, so a Rejected read of the same opening and environment
   * asked before it finished (`doneAt`; unset while the DELETE is in flight) must not list the person
   * again. A new rejection of theirs forgets it.
   */
  rejectedRestores: Record<string, { at: number; doneAt?: number }>;
  /** Suggested by TG — Slack thread replies + Fast Track nominations for this
   * opportunity, from GET /:jobOpeningId/tg-suggestions. Rendered on the
   * matching page's 3rd tab; each entry carries a `suggestion` provenance
   * object + live `isShortlisted`. */
  tgSuggestions: any[];
  tgSuggestionIds: string[];
  tgSuggestionCount: number;
  tgSuggestionsLoading: boolean;
  /** False until the feedback-to-TG table exists in this environment (2026-10-09). */
  tgFeedbackAvailable: boolean;
  companyFilters: any;
  aggregates: { role: Record<string, number> };
  addShortlistLoading: boolean;
  removeShortlistLoading: boolean;
  /**
   * The latest shortlist request per person, keyed `${environment}:${opening}|${id}`
   * (2026-10-28): the LAST one asked wins. An add whose own Undo asked for a removal after it,
   * or a reload asked before a removal finished, must not mark the person shortlisted again (a
   * rejection made meanwhile looked "shortlisted elsewhere" and was dropped unsaved); a re-add
   * asked after a removal does count. Actions without a `scope` skip this.
   */
  shortlistIntent: Record<string, ShortlistIntent>;
}

/** One person's latest shortlist request on one opening and environment. */
export interface ShortlistIntent {
  kind: "add" | "remove";
  /** When it was asked (ms). */
  at: number;
  /** A removal: when it finished. */
  doneAt?: number;
}

const intentKey = (scope: string | undefined, id: string) => (scope ? `${scope}|${id}` : null);

/** The trays' "Loading…" for the Rejected list (2026-10-28): only while its FIRST read is pending,
 * so a background reload over an empty list doesn't flip the text. */
export const rejectedFirstLoad = (matching: Pick<MatchingState, "rejectedLoading" | "rejectedLoaded"> | undefined) =>
  Boolean(matching?.rejectedLoading) && !matching?.rejectedLoaded;

function emptySearchSlice(pageSize: number): SearchSliceState {
  return {
    candidates: [],
    candidatesLoading: false,
    candidatesError: null,
    pagination: { total_results: 0, total_pages: 0, page_size: pageSize, page: 1 },
    currentPage: 1,
    parsedJd: null,
    searchDebug: null,
    sortMode: null,
    m6: null,
    evidenceRank: null,
    sessionId: null,
    viewId: null,
  };
}

const initialState: MatchingState = {
  jobDetails: null,
  detailsLoading: false,
  detailsError: null,
  search: emptySearchSlice(25),
  shortlist: [],
  shortlistIds: [],
  shortlistCount: 0,
  shortlistLoading: false,
  rejected: [],
  rejectedIds: [],
  rejectedCount: 0,
  rejectedLoading: false,
  rejectedLoaded: false,
  rejectedRestores: {},
  tgSuggestions: [],
  tgSuggestionIds: [],
  tgSuggestionCount: 0,
  tgSuggestionsLoading: false,
  tgFeedbackAvailable: true,
  companyFilters: null,
  aggregates: { role: {} },
  addShortlistLoading: false,
  removeShortlistLoading: false,
  shortlistIntent: {},
};

export default function matchingReducer(
  state = initialState,
  action: MatchingAction,
): MatchingState {
  switch (action.type) {
    case MATCHING_DETAILS_REQUEST:
      return { ...state, detailsLoading: true, detailsError: null };
    case MATCHING_DETAILS_SUCCESS:
      return { ...state, detailsLoading: false, jobDetails: action.payload };
    case MATCHING_DETAILS_FAILURE:
      return { ...state, detailsLoading: false, detailsError: action.payload };

    case MATCHING_SEARCH_REQUEST:
      return {
        ...state,
        search: {
          ...state.search,
          candidatesLoading: true,
          candidatesError: null,
        },
      };
    case MATCHING_SEARCH_SUCCESS: {
      const page = action.payload.pagination.page ?? state.search.currentPage;
      return {
        ...state,
        search: {
          ...state.search,
          candidatesLoading: false,
          candidates: action.payload.candidates.map((c: any, i: number) => ({
            ...c,
            _originalRank: i + 1 + (page - 1) * (action.payload.pagination.page_size || 25),
            ...(action.payload.viewId ? { _viewId: action.payload.viewId } : {}),
            _originalTotalResults: action.payload.pagination.total_results,
          })),
          pagination: action.payload.pagination,
          currentPage: page,
          parsedJd: action.payload.parsedJd ?? null,
          searchDebug: action.payload.searchDebug ?? null,
          sortMode: action.payload.sortMode ?? null,
          m6: action.payload.m6 ?? null,
          evidenceRank: action.payload.evidenceRank ?? null,
          sessionId: action.payload.sessionId ?? null,
          viewId: action.payload.viewId ?? null,
        },
      };
    }
    case MATCHING_SEARCH_FAILURE:
      return {
        ...state,
        search: {
          ...state.search,
          candidatesLoading: false,
          candidatesError: action.payload.message,
        },
      };

    case MATCHING_SHORTLIST_REQUEST:
      return { ...state, shortlistLoading: true };
    case MATCHING_SHORTLIST_SUCCESS: {
      const asked = action.requestedAt;
      // Off the list: a removal was the latest request and it is still in flight, or it
      // finished after this reload was asked (the answer may predate it on the server).
      const out = (id: string) => {
        const key = intentKey(action.scope, id);
        const intent = key ? state.shortlistIntent[key] : undefined;
        if (intent?.kind !== "remove") return false;
        return intent.doneAt === undefined || (asked !== undefined && asked < intent.doneAt);
      };
      const list = action.payload.list.filter((c: any) => !out(c.id));
      const ids = action.payload.ids.filter((id: string) => !out(id));
      return {
        ...state,
        shortlistLoading: false,
        shortlist: list,
        shortlistIds: ids,
        shortlistCount: Math.max(0, action.payload.count - (action.payload.ids.length - ids.length)),
      };
    }
    case MATCHING_SHORTLIST_FAILURE:
      return { ...state, shortlistLoading: false };

    case MATCHING_REJECTED_REQUEST:
      return { ...state, rejectedLoading: true };
    case MATCHING_REJECTED_SUCCESS: {
      const asked = action.requestedAt;
      // Off the list: a restore still in flight, or one that finished after this read was asked.
      const restores = state.rejectedRestores;
      const pending = (r: { doneAt?: number }) => r.doneAt === undefined || asked === undefined || asked < r.doneAt;
      const out = (id: string) => {
        const key = intentKey(action.scope, id);
        const r = key ? restores[key] : undefined;
        return Boolean(r && pending(r));
      };
      const list = action.payload.list.filter((r: any) => !out(r.id));
      const ids = action.payload.ids.filter((id: string) => !out(id));
      // A finished restore this read already reflects is no longer needed (other scopes stay).
      const prefix = action.scope ? `${action.scope}|` : null;
      const kept = Object.fromEntries(
        Object.entries(restores).filter(([key, r]) => !prefix || !key.startsWith(prefix) || pending(r)),
      );
      return {
        ...state,
        rejectedLoading: false,
        rejectedLoaded: true,
        rejected: list,
        rejectedIds: ids,
        rejectedCount: Math.max(0, action.payload.count - (action.payload.ids.length - ids.length)),
        rejectedRestores: Object.keys(kept).length === Object.keys(restores).length ? restores : kept,
      };
    }
    case MATCHING_REJECTED_FAILURE:
      return { ...state, rejectedLoading: false, rejectedLoaded: true };

    // Optimistic restore: the person leaves the Rejected list on click.
    case MATCHING_RESTORE_REJECTED_REQUEST: {
      const id = action.payload;
      const had = state.rejectedIds.includes(id);
      return {
        ...state,
        rejected: had ? state.rejected.filter((r) => r.id !== id) : state.rejected,
        rejectedIds: had ? state.rejectedIds.filter((x) => x !== id) : state.rejectedIds,
        rejectedCount: had ? Math.max(0, state.rejectedCount - 1) : state.rejectedCount,
        rejectedRestores: { ...state.rejectedRestores, [`${action.scope}|${id}`]: { at: action.at } },
      };
    }
    case MATCHING_RESTORE_REJECTED_SUCCESS: {
      const key = `${action.scope}|${action.payload}`;
      const r = state.rejectedRestores[key];
      // Rejected again since: that rejection stands.
      if (!r) return state;
      return { ...state, rejectedRestores: { ...state.rejectedRestores, [key]: { ...r, doneAt: action.at } } };
    }
    case MATCHING_RESTORE_REJECTED_FAILURE: {
      // It didn't happen: forget it (the thunk reloads the list).
      const key = `${action.scope}|${action.payload}`;
      if (!state.rejectedRestores[key]) return state;
      const { [key]: _gone, ...rest } = state.rejectedRestores;
      return { ...state, rejectedRestores: rest };
    }

    case MATCHING_TG_SUGGESTIONS_REQUEST:
      return { ...state, tgSuggestionsLoading: true };
    case MATCHING_TG_SUGGESTIONS_SUCCESS:
      return {
        ...state,
        tgSuggestionsLoading: false,
        tgSuggestions: action.payload.list,
        tgSuggestionIds: action.payload.ids,
        tgSuggestionCount: action.payload.count,
        tgFeedbackAvailable: action.payload.feedbackAvailable !== false,
      };
    case MATCHING_TG_SUGGESTIONS_FAILURE:
      return { ...state, tgSuggestionsLoading: false };

    // Optimistic skip: remove the card (mirrors MATCHING_ADD_SHORTLIST_SUCCESS)
    // AND prepend a provisional entry to the rejected list so the review rail
    // updates on click. The fetchRejected the thunk chains then replaces the
    // whole list with server rows.
    case MATCHING_SKIP_CANDIDATE: {
      const { candidateId, reason, reasons, reasonNote, candidate, rejectedAt } = action.payload;
      const nextSearch = {
        ...state.search,
        candidates: state.search.candidates.filter((c) => c.id !== candidateId),
      };
      // Rejected again: an earlier restore of theirs on this opening and environment no longer
      // hides them. A restore elsewhere still does.
      const restoreKey = `${action.scope}|${candidateId}`;
      let rejectedRestores = state.rejectedRestores;
      if (restoreKey in rejectedRestores) {
        const { [restoreKey]: _gone, ...rest } = rejectedRestores;
        rejectedRestores = rest;
      }
      if (state.rejectedIds.includes(candidateId)) {
        return { ...state, search: nextSearch, rejectedRestores };
      }
      const name =
        candidate?.name ||
        [candidate?.first_name, candidate?.last_name].filter(Boolean).join(' ') ||
        null;
      const entry = {
        id: candidateId,
        first_name: candidate?.first_name ?? null,
        last_name: candidate?.last_name ?? null,
        name,
        reason: reason ?? null,
        reasons: reasons ?? null,
        reason_note: reasonNote ?? null,
        rejected_at: rejectedAt,
      };
      return {
        ...state,
        search: nextSearch,
        rejected: [entry, ...state.rejected],
        rejectedIds: [candidateId, ...state.rejectedIds],
        rejectedCount: state.rejectedCount + 1,
        rejectedRestores,
      };
    }

    case MATCHING_ADD_SHORTLIST_REQUEST: {
      const key = action.candidateId ? intentKey(action.scope, action.candidateId) : null;
      return {
        ...state,
        addShortlistLoading: true,
        shortlistIntent: key ? { ...state.shortlistIntent, [key]: { kind: "add", at: action.at ?? Date.now() } } : state.shortlistIntent,
      };
    }
    // Optimistic shortlist add: drop the card AND register the id in
    // `shortlistIds` immediately. The thunk refetches the full shortlist right
    // after, but that roundtrip used to leave a window in which the candidate
    // was in no exclusion list at all — a search landing inside it (a filter
    // tweak, a sort flip, the swipe deck's fetch-ahead) dealt them again, which
    // is how the same person could be actioned twice in one session.
    // Shortlisting also supersedes a prior rejection (mirrors the server's
    // getRejectedCandidates, which hides shortlisted candidates), so the review
    // rail's Rejected entry is cleared here too.
    case MATCHING_ADD_SHORTLIST_SUCCESS: {
      const candidateId = action.payload;
      // A removal asked after this add (its own Undo) wins over it.
      const key = intentKey(action.scope, candidateId);
      const intent = key ? state.shortlistIntent[key] : undefined;
      if (intent?.kind === "remove" && (action.requestedAt === undefined || intent.at > action.requestedAt)) {
        return { ...state, addShortlistLoading: false };
      }
      const wasRejected = state.rejectedIds.includes(candidateId);
      return {
        ...state,
        addShortlistLoading: false,
        search: {
          ...state.search,
          candidates: state.search.candidates.filter((c) => c.id !== candidateId),
        },
        shortlistIds: state.shortlistIds.includes(candidateId)
          ? state.shortlistIds
          : [...state.shortlistIds, candidateId],
        shortlistCount: state.shortlistIds.includes(candidateId)
          ? state.shortlistCount
          : state.shortlistCount + 1,
        rejected: wasRejected ? state.rejected.filter((r) => r.id !== candidateId) : state.rejected,
        rejectedIds: wasRejected
          ? state.rejectedIds.filter((id) => id !== candidateId)
          : state.rejectedIds,
        rejectedCount: wasRejected ? Math.max(0, state.rejectedCount - 1) : state.rejectedCount,
      };
    }
    case MATCHING_ADD_SHORTLIST_FAILURE:
      return { ...state, addShortlistLoading: false };

    case MATCHING_REMOVE_SHORTLIST_REQUEST: {
      const key = action.payload ? intentKey(action.scope, action.payload) : null;
      return {
        ...state,
        removeShortlistLoading: true,
        shortlistIntent: key ? { ...state.shortlistIntent, [key]: { kind: "remove", at: action.at ?? Date.now() } } : state.shortlistIntent,
      };
    }
    case MATCHING_REMOVE_SHORTLIST_SUCCESS:
      return {
        ...state,
        removeShortlistLoading: false,
        shortlist: state.shortlist.filter((c) => c.id !== action.payload),
        shortlistIds: state.shortlistIds.filter((id) => id !== action.payload),
        shortlistCount: Math.max(0, state.shortlistCount - 1),
        shortlistIntent: (() => {
          const key = intentKey(action.scope, action.payload);
          if (!key) return state.shortlistIntent;
          const intent = state.shortlistIntent[key];
          const doneAt = action.at ?? Date.now();
          // A removal with no REQUEST (the Shortlisted tab's dialog) brings the time it was asked:
          // an add asked before that is superseded, a re-add asked since stays the latest request.
          if (intent?.kind === "add" && (action.requestedAt === undefined || intent.at > action.requestedAt)) {
            return state.shortlistIntent;
          }
          const at = intent?.kind === "remove" ? intent.at : (action.requestedAt ?? doneAt);
          return { ...state.shortlistIntent, [key]: { kind: "remove", at, doneAt } };
        })(),
      };
    case MATCHING_REMOVE_SHORTLIST_FAILURE:
      return {
        ...state,
        removeShortlistLoading: false,
        // The removal didn't happen: forget it (the thunk reloads the shortlist).
        shortlistIntent: (() => {
          const key = action.candidateId ? intentKey(action.scope, action.candidateId) : null;
          if (!key || state.shortlistIntent[key]?.kind !== "remove") return state.shortlistIntent;
          const { [key]: _gone, ...rest } = state.shortlistIntent;
          return rest;
        })(),
      };

    case MATCHING_AGGREGATES_SUCCESS:
      return { ...state, aggregates: action.payload };
    case MATCHING_COMPANY_FILTERS_SUCCESS:
      return { ...state, companyFilters: action.payload };

    case MATCHING_SET_PAGE:
      return {
        ...state,
        search: {
          ...state.search,
          currentPage: action.payload.page,
        },
      };

    case MATCHING_MARK_VIEWED: {
      const { candidateId, viewedAt } = action.payload;
      const stamp = (list: any[]) => {
        let touched = false;
        const next = list.map((c: any) => {
          if (c.id !== candidateId) return c;
          touched = true;
          return {
            ...c,
            viewed_at: viewedAt,
            view_count: (c.view_count ?? 0) + 1,
          };
        });
        return touched ? next : list;
      };
      const next = stamp(state.search.candidates);
      if (next === state.search.candidates) return state;
      return {
        ...state,
        search: { ...state.search, candidates: next },
      };
    }

    case MATCHING_CLEAR:
      return { ...initialState };

    default:
      return state;
  }
}
